import classNames from 'classnames';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface BaseInputProps {
  label?: string;
  id?: string;
  variant?: 'form' | 'inline' | undefined;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  value: string;
  autoResize?: boolean;
  minWidth?: string | number;
  onChangeText?: (value: string) => void;
  rows?: number;
}

type InputProps = BaseInputProps &
  (
    | (Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & { rows?: never })
    | (Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> & { rows: number })
  );

const variantStyles: Record<string, string> = {
  form: 'px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
  inline:
    'border-b border-gray-300 bg-transparent px-0 py-1 focus:outline-none focus:border-blue-500 transition-colors',
};

const Input: React.FC<InputProps> = ({
  label,
  id = 'auto-size-input',
  className,
  labelClassName,
  variant,
  inputClassName,
  onChangeText,
  autoResize,
  minWidth,
  value,
  rows,
  ...inputProps
}) => {
  const [state, setState] = useState<string>(value || '');
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const isUserTyping = useRef<boolean>(false);
  const currentValue = useRef<string>(value || '');

  const debounceUpdate = useMemo(
    () =>
      debounce((value: string) => {
        // Only update if this is still the current value
        if (value === currentValue.current && onChangeText) {
          onChangeText(value);
        }
      }, 1000),
    [onChangeText],
  );

  const resizeInput = useCallback(() => {
    if (autoResize && ref.current) {
      if (rows) {
        // For textarea, resize height
        ref.current.style.height = 'auto';
        ref.current.style.height = ref.current.scrollHeight + 'px';
      } else {
        // For input, resize width
        ref.current.style.width = '0px'; // Reset width to shrink if needed
        const scrollWidth = ref.current.scrollWidth;
        // Add extra padding (20px) to account for custom fonts and prevent cutoff
        const newWidth = Math.max(
          scrollWidth + 20,
          minWidth ? (typeof minWidth === 'number' ? minWidth : parseInt(minWidth)) : 0,
        );
        ref.current.style.width = newWidth + 'px';
      }
    }
  }, [autoResize, rows, minWidth]);

  useEffect(() => {
    // Only sync external value changes when user is not actively typing
    if (value !== state && !isUserTyping.current) {
      setState(value || '');
    }
    resizeInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onChange = useCallback(
    value => {
      const newValue = value || '';
      currentValue.current = newValue;
      setState(newValue);
      resizeInput();

      // For empty strings, immediately update parent and don't block external sync
      if (newValue === '') {
        isUserTyping.current = false;
        if (onChangeText) {
          onChangeText(newValue);
        }
      } else {
        isUserTyping.current = true;
        debounceUpdate(newValue);
        // Reset typing flag after debounce delay + small buffer
        setTimeout(() => {
          isUserTyping.current = false;
        }, 1100);
      }
    },
    [debounceUpdate, resizeInput, onChangeText],
  );

  return (
    <div className={classNames(className, 'flex flex-col sm:flex-row justify-start items-start sm:items-center gap-4')}>
      {label && (
        <label htmlFor={id} className={classNames(labelClassName)}>
          {label}
        </label>
      )}

      {rows ? (
        <textarea
          id={id}
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          onChange={e => onChange(e.target.value)}
          className={classNames({ 'inline-block': autoResize }, inputClassName, variantStyles[variant])}
          style={{ minWidth: minWidth }}
          value={state}
          rows={rows}
          {...(inputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          id={id}
          ref={ref as React.RefObject<HTMLInputElement>}
          onChange={e => onChange(e.target.value)}
          className={classNames({ 'inline-block': autoResize }, inputClassName, variantStyles[variant])}
          style={{ minWidth: minWidth }}
          value={state}
          {...(inputProps as React.InputHTMLAttributes<HTMLInputElement>)}
        />
      )}
    </div>
  );
};

export default Input;
